'use client';

import { usePulseTrackerOptional } from '@cineamo/pulse/src/react/PulseProvider';
import React, { useState } from 'react';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { cn } from '../../helpers/cn';
import type { CustomSVGProps } from '../../icons/CustomSVGProps.types';
import { IconFsk0, IconFsk12, IconFsk16, IconFsk18, IconFsk6 } from '../../icons/ic-fsk';

import FskInfoModal from './FskInfoModal';

const ageRatingToComponent: Record<string, React.ComponentType<CustomSVGProps>> = {
    '0': IconFsk0,
    '6': IconFsk6,
    '12': IconFsk12,
    '16': IconFsk16,
    '18': IconFsk18
};

export type FskLabelProps = {
    ageRating: string;
    /** Reported with the click event. */
    movieTitle?: string;
    /** Numeric movie id; when given, opening the info modal also reports a pulse view. */
    movieId?: number;
    onClick?: () => void;
    useInfoModal?: boolean;
    /** Forces the info modal's artwork; unset, it follows the resolved theme. */
    isThemeLight?: boolean;
    options?: {
        classNameIcon?: string;
    };
};

/**
 * Age-rating badge (FSK). Opens the FSK info modal on click unless `useInfoModal` is turned off —
 * export/preview surfaces render the icon alone.
 */
const FskLabel: React.FC<FskLabelProps> = ({
    ageRating,
    movieTitle,
    movieId,
    onClick,
    useInfoModal = true,
    isThemeLight,
    options
}) => {
    const [fskInfoModalIsOpen, setFskInfoModalIsOpen] = useState(false);
    const { track } = useAnalytics();
    // Optional: the operator apps render this badge without ever mounting a PulseProvider.
    const pulseTracker = usePulseTrackerOptional();
    const IconComponent = ageRatingToComponent[ageRating];

    if (!IconComponent) {
        // Unknown/invalid ageRating (e.g. free-text placeholder on custom content) — render nothing,
        // matching how regular content behaves when no certification is known.
        return null;
    }

    function openInfoModal(): void {
        track('FSK Button Clicked', { movie: movieTitle || 'N/A', rating: ageRating });
        if (movieId) {
            pulseTracker?.track({ movie_id: movieId, event_type: 'movie_age_rating_view' });
        }
        setFskInfoModalIsOpen(true);
    }

    return (
        // `flex` keeps the badge a block-level box. As an inline-level box it would sit on its
        // wrapper's baseline, and the line box would reserve descender space below the icon —
        // enough to push the badge above the centre of the text it is aligned with.
        <span className="pointer-events-auto flex flex-none items-center">
            <IconComponent
                className={cn(options?.classNameIcon, {
                    'cursor-pointer': useInfoModal
                })}
                onClick={(event: React.MouseEvent) => {
                    // When the icon has its own action (info modal or onClick), stop the click from
                    // bubbling so a clickable ancestor (e.g. a list-item row) doesn't also fire.
                    if (useInfoModal || onClick) {
                        event.stopPropagation();
                        event.preventDefault();
                    }
                    onClick?.();
                    if (useInfoModal) openInfoModal();
                }}
            />

            {useInfoModal && fskInfoModalIsOpen && (
                <FskInfoModal
                    modalIsOpen={fskInfoModalIsOpen}
                    setModalIsOpen={setFskInfoModalIsOpen}
                    onClose={() => setFskInfoModalIsOpen(false)}
                    isThemeLight={isThemeLight}
                />
            )}
        </span>
    );
};

export default FskLabel;
